/**
 * Review — the human gate. Every rule an agent drafted waits here, with the
 * sentence it came from. Approve writes it (effective-dated); Reject keeps it
 * on record with the reason; Edit & approve saves a corrected value as a new
 * proposal from the same source, approves that, and rejects the draft as
 * superseded.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ExternalLink, Pencil, RefreshCw, X } from 'lucide-react';
import { funderAgentsApi, type RuleProposal } from '../../../shared/services/funderAgentsApi';
import { Btn, Empty, ErrorLine, NoteForm, ValueEditor, errText, fmtDate, showValue, type Notify } from './shared';

export default function ReviewView({ notify, onDecided }: { notify: Notify; onDecided?: () => void }) {
  const [items, setItems] = useState<RuleProposal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await funderAgentsApi.proposals({ status: 'pending' }));
    } catch (e) {
      setError(errText(e));
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => {
    const m = new Map<string, RuleProposal[]>();
    (items ?? []).forEach((p) => {
      const k = `${p.payer_id}|${p.payer_name}`;
      m.set(k, [...(m.get(k) ?? []), p]);
    });
    return Array.from(m.entries());
  }, [items]);

  const done = (id: number, text: string) => {
    setItems((list) => (list ?? []).filter((p) => p.id !== id));
    notify(true, text);
    onDecided?.();
  };

  const approve = async (p: RuleProposal) => {
    if (!window.confirm(`Approve ${p.rule_key} = ${showValue(p.rule_value)} for ${p.payer_name} (${p.program_type})?\n\nIt applies to claims from its effective date.`)) return;
    setBusy(p.id);
    try {
      await funderAgentsApi.approve(p.id);
      done(p.id, `Approved: ${p.rule_key} for ${p.payer_name}.`);
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const reject = async (p: RuleProposal, note: string) => {
    try {
      await funderAgentsApi.reject(p.id, note);
      setRejecting(null);
      done(p.id, `Rejected: ${p.rule_key} for ${p.payer_name}.`);
    } catch (e) {
      notify(false, errText(e));
    }
  };

  const editAndApprove = async (p: RuleProposal, value: unknown, note: string) => {
    try {
      const created = await funderAgentsApi.createProposal({
        payer_id: p.payer_id,
        program_type: p.program_type,
        rule_key: p.rule_key,
        rule_value: value,
        effective_date: p.effective_date ?? null,
        source_url: p.source_url ?? null,
        source_excerpt: p.source_excerpt ?? null,
        rationale: note,
      });
      await funderAgentsApi.approve(created.id, note);
      await funderAgentsApi.reject(p.id, `Superseded by edit (proposal ${created.id}): ${note}`);
      setEditing(null);
      done(p.id, `Corrected and approved: ${p.rule_key} for ${p.payer_name}.`);
    } catch (e) {
      notify(false, errText(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-500">
          {items ? `${items.length} draft${items.length === 1 ? '' : 's'} waiting for a person.` : 'Loading…'} Check each value against the quoted sentence.
        </p>
        <Btn tone="ghost" title="Refresh" onClick={() => void load()}>
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
        </Btn>
      </div>
      <ErrorLine text={error} />
      {items !== null && items.length === 0 && !error && (
        <Empty>Nothing waiting. Draft rules for a plan from the Plans view, or run the builder from Runs.</Empty>
      )}
      {groups.map(([key, list]) => (
        <section key={key} className="rounded-md border border-v2border bg-surface">
          <h4 className="px-3 py-2 border-b border-v2border text-sm font-semibold text-ink">
            {list[0].payer_name} <span className="font-normal text-ink-500">· {list.length} draft{list.length === 1 ? '' : 's'}</span>
          </h4>
          <ul className="divide-y divide-v2border">
            {list.map((p) => (
              <li key={p.id} className="p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-paper-2 text-ink-700">{p.program_type}</span>
                  <code className="font-semibold text-ink">{p.rule_key}</code>
                  <span className="text-ink-500">=</span>
                  <code className="rounded bg-paper-2 px-1.5 py-0.5 text-ink">{showValue(p.rule_value)}</code>
                  <span className="text-xs text-ink-500">
                    {p.proposed_by === 'agent' ? 'Drafted by agent' : 'Proposed by a person'} · {fmtDate(p.created_ts)}
                    {p.effective_date ? ` · effective ${p.effective_date}` : ''}
                  </span>
                </div>
                {p.source_excerpt ? (
                  <blockquote className="border-l-2 border-v2accent pl-3 text-sm text-ink-700 italic">“{p.source_excerpt}”</blockquote>
                ) : (
                  <p className="text-xs text-attention-ink">No quoted source sentence — check this one by hand.</p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-xs text-ink-500">
                  {p.source_url && (
                    <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-v2accent-ink hover:underline">
                      Source <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    </a>
                  )}
                  {p.rationale && <span>{p.rationale}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Btn tone="primary" busy={busy === p.id} onClick={() => void approve(p)}>
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                    Approve
                  </Btn>
                  <Btn
                    onClick={() => {
                      setEditing(editing === p.id ? null : p.id);
                      setRejecting(null);
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                    Edit &amp; approve
                  </Btn>
                  <Btn
                    tone="danger"
                    onClick={() => {
                      setRejecting(rejecting === p.id ? null : p.id);
                      setEditing(null);
                    }}
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                    Reject
                  </Btn>
                </div>
                {rejecting === p.id && (
                  <NoteForm
                    label="Why reject it? (kept on record)"
                    placeholder="e.g. The manual applies this to CS only, not ECM"
                    submitLabel="Reject draft"
                    onSubmit={(note) => reject(p, note)}
                    onCancel={() => setRejecting(null)}
                  />
                )}
                {editing === p.id && (
                  <ValueEditor
                    initial={p.rule_value}
                    submitLabel="Save correction and approve"
                    onSubmit={(value, note) => editAndApprove(p, value, note)}
                    onCancel={() => setEditing(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
